/**
 * Pack fallback modal
 * When a sold-out pack (Simple Bundles) is submitted, lists its components with their
 * availability and adds the available ones to the cart instead of the pack.
 * Opened by the `pack:fallback-open` event (detail: { form, quantity, button }).
 * Stock quantities are used for computation only — never displayed.
 */
(function () {
  if (window.packFallbackInit) return;
  window.packFallbackInit = true;

  var STRINGS = window.packFallbackStrings || {};
  var THEME_STRINGS = (window.themeVariables && window.themeVariables.strings) || {};
  var TOAST_ADDED = THEME_STRINGS.toastAddedToCart || 'Ajouté au panier';
  var TOAST_ADD_ERROR = THEME_STRINGS.toastAddError || 'Erreur lors de l’ajout';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var state = null; // { modal, form, button, items }

  document.addEventListener('pack:fallback-open', function (e) {
    var detail = e.detail || {};
    if (!detail.form) return;
    openModal(detail.form, detail.quantity || 1, detail.button);
  });

  function getPackData(form) {
    var script = form.querySelector('[data-pack-fallback-data]');
    if (!script) return null;
    try { return JSON.parse(script.textContent); } catch (err) { return null; }
  }

  function openModal(form, packQty, button) {
    // getAttribute: `form.id` is shadowed by the form's <input name="id">
    var modal = document.querySelector('[data-pack-modal][data-form-id="' + form.getAttribute('id') + '"]');
    var data = getPackData(form);
    if (!modal || !data || !data.components) return;

    if (button) button.disabled = true;

    fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .catch(function () { return { items: [] }; })
      .then(function (cart) {
        var inCart = {};
        (cart.items || []).forEach(function (line) {
          inCart[line.variant_id] = (inCart[line.variant_id] || 0) + line.quantity;
        });

        var items = data.components.map(function (component) {
          var needed = component.qty * packQty;
          var free = Math.max(component.stock - (inCart[component.id] || 0), 0);
          var addable = Math.min(needed, free);
          return {
            id: component.id,
            title: component.title,
            price: component.price,
            needed: needed,
            addable: addable,
            status: addable === needed ? 'available' : (addable > 0 ? 'partial' : 'unavailable')
          };
        });

        state = { modal: modal, form: form, button: button, items: items };
        render(modal, items);
        bindModal(modal);
        modal.showModal();
      });
  }

  function render(modal, items) {
    var list = modal.querySelector('[data-pack-modal-list]');
    var intro = modal.querySelector('[data-pack-modal-intro]');
    var totalRow = modal.querySelector('[data-pack-modal-total-row]');
    var totalEl = modal.querySelector('[data-pack-modal-total]');
    var confirmBtn = modal.querySelector('.pack-modal__confirm');

    list.innerHTML = '';
    var total = 0;
    var addableCount = 0;

    items.forEach(function (item) {
      total += item.price * item.addable;
      if (item.addable > 0) addableCount++;

      var li = document.createElement('li');
      li.className = 'pack-modal__item pack-modal__item--' + item.status;

      var name = document.createElement('span');
      name.className = 'pack-modal__item-name';
      name.textContent = item.title + ' ';
      var qty = document.createElement('span');
      qty.className = 'pack-modal__item-qty';
      qty.textContent = '(x' + item.needed + ')';
      name.appendChild(qty);

      var status = document.createElement('span');
      status.className = 'pack-modal__status pack-modal__status--' + item.status;
      status.textContent = STRINGS[item.status] || item.status;

      li.appendChild(name);
      li.appendChild(status);
      list.appendChild(li);
    });

    intro.textContent = addableCount > 0 ? STRINGS.intro : STRINGS.noneAvailable;
    totalRow.hidden = addableCount === 0;
    totalEl.textContent = formatMoney(total);
    confirmBtn.disabled = addableCount === 0;
  }

  function bindModal(modal) {
    if (modal.hasAttribute('data-bound')) return;
    modal.setAttribute('data-bound', '');

    modal.querySelectorAll('[data-pack-modal-close], .pack-modal__cancel').forEach(function (btn) {
      btn.addEventListener('click', function () { modal.close(); });
    });

    // Click on the backdrop closes the modal
    modal.addEventListener('click', function (e) {
      if (e.target === modal) modal.close();
    });

    // Fires for Escape, close buttons and after a successful add
    modal.addEventListener('close', function () {
      if (state && state.button) state.button.disabled = false;
      state = null;
    });

    modal.querySelector('.pack-modal__confirm').addEventListener('click', function () {
      if (!state) return;
      addToCart(this, state.items.filter(function (item) { return item.addable > 0; }));
    });
  }

  function addToCart(confirmBtn, items) {
    var current = state;
    confirmBtn.disabled = true;

    var payload = items.map(function (item) { return { id: item.id, quantity: item.addable }; });

    postAdd(payload)
      .catch(function () {
        // Bulk add is all-or-nothing: retry line by line so one failing component doesn't block the others
        return payload.reduce(function (chain, line) {
          return chain.then(function (added) {
            return postAdd([line]).then(function () { return added + 1; }, function () { return added; });
          });
        }, Promise.resolve(0)).then(function (added) {
          if (!added) throw new Error(TOAST_ADD_ERROR);
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:main-product-added', { bubbles: true }));
        refreshCartCount();
        confirmBtn.disabled = false;
        current.modal.close();
        showToast(TOAST_ADDED);
      })
      .catch(function (err) {
        confirmBtn.disabled = false;
        showToast(err.message || TOAST_ADD_ERROR, true);
      });
  }

  function postAdd(lines) {
    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: lines })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (data) { throw new Error(data.description || TOAST_ADD_ERROR); });
      return res.json();
    });
  }

  function refreshCartCount() {
    fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.removeAttribute('hidden');
        });
      })
      .catch(function () {});
  }

  function formatMoney(cents) {
    var format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '{{amount_with_comma_separator}} €';
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    var withSpaces = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      switch (key) {
        case 'amount': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
        case 'amount_no_decimals': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        case 'amount_with_comma_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
        case 'amount_no_decimals_with_comma_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        case 'amount_with_space_separator': return withSpaces + ',' + parts[1];
        case 'amount_with_apostrophe_separator': return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\'') + '.' + parts[1];
        default: return amount;
      }
    });
  }

  function showToast(message, isError) {
    if (!document.querySelector('.quick-add-toast-styles')) {
      var style = document.createElement('style');
      style.className = 'quick-add-toast-styles';
      style.textContent = '.quick-add-toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%) translateY(20px);z-index:2147483647;background:#31af54;color:#fff;font-size:14px;font-weight:600;padding:12px 24px;border-radius:999px;box-shadow:0 4px 20px rgba(0,0,0,.12);opacity:0;animation:quickAddToastIn .3s ease forwards,quickAddToastOut .3s ease 2.2s forwards;pointer-events:none}.quick-add-toast--error{background:#dc2626}@keyframes quickAddToastIn{to{opacity:1;transform:translateX(-50%) translateY(0)}}@keyframes quickAddToastOut{to{opacity:0;transform:translateX(-50%) translateY(10px)}}';
      document.head.appendChild(style);
    }
    var toast = document.createElement('div');
    toast.className = 'quick-add-toast' + (isError ? ' quick-add-toast--error' : '');
    toast.setAttribute('role', 'status');
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 2600);
  }
})();
